<template>
    <div class="modal" tabindex="-1" role="dialog" :id="modalId">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content confirm-by-typing">
                <div class="confirm-by-typing-header">
                    <fa icon="exclamation-triangle" class="confirm-by-typing-header-icon" />
                    <span class="confirm-by-typing-header-title">{{ title }}</span>
                </div>

                <div class="modal-body">
                    <slot />

                    <p class="confirm-by-typing-prompt">
                        Para prosseguir, digite <strong>{{ confirmationWord }}</strong> abaixo.
                    </p>

                    <input
                        v-model="confirmText"
                        type="text"
                        class="form-control confirm-by-typing-input"
                        :class="{'is-confirmed': isConfirmed}"
                        :placeholder="confirmationWord"
                        autocomplete="off"
                        spellcheck="false"
                        @keyup.enter="confirmIfAllowed()" />

                    <p class="confirm-by-typing-hint" v-if="confirmHint">{{ confirmHint }}</p>
                </div>

                <div class="modal-footer confirm-by-typing-footer">
                    <button type="button" class="btn btn-outline-secondary" @click="cancel()">
                        Cancelar
                    </button>

                    <button type="button" class="btn btn-danger" :disabled="!isConfirmed" @click="confirm()">
                        {{ confirmLabel }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import modalService from '@/utils/services/ModalService'

export default {
    name: 'ConfirmByTypingModalCmp',
    props: {
        modalId: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            default: 'Aviso de cautela',
        },
        confirmationWord: {
            type: String,
            default: 'CONFIRMO',
        },
        confirmLabel: {
            type: String,
            default: 'Confirmar',
        },
        confirmHint: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            confirmText: '',
        }
    },
    computed: {
        /** Mesma regra de ChannelCreationModel.isValid(). */
        isConfirmed() {
            return this.confirmText.toUpperCase().trim() === this.confirmationWord.toUpperCase().trim()
        },
    },
    watch: {
        // A palavra é sempre exibida em caixa alta, então o campo acompanha
        // enquanto o usuário digita, em vez de só aceitar no final.
        confirmText(value) {
            const upper = value.toUpperCase()

            if (upper !== value) {
                this.confirmText = upper
            }
        },
    },
    methods: {
        show() {
            this.confirmText = ''
            modalService.show(`#${this.modalId}`)
        },
        hide() {
            this.confirmText = ''
            modalService.hide(`#${this.modalId}`)
        },
        // Emite só depois do modal fechar de fato: quem escuta o confirm pode
        // abrir outro modal em seguida.
        async confirm() {
            this.confirmText = ''
            await modalService.hideAndWait(`#${this.modalId}`)
            this.$emit('confirm')
        },
        confirmIfAllowed() {
            if (this.isConfirmed) {
                this.confirm()
            }
        },
        cancel() {
            this.$emit('cancel')
            this.hide()
        },
    },
}
</script>

<style lang="scss" scoped>
$danger: var(--danger-solid);
$confirmed: var(--success-solid);

.confirm-by-typing {
    border: none;
}

.confirm-by-typing-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 20px;
    background-color: $danger;
    border-radius: 3px 3px 0 0;
}

.confirm-by-typing-header-icon {
    color: var(--text-on-brand);
    font-size: 1.1em;
}

.confirm-by-typing-header-title {
    color: var(--text-on-brand);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: .9em;
}

.modal-body {
    padding: 20px;
}

.confirm-by-typing-prompt {
    margin-top: 20px;
    margin-bottom: 8px;
    color: var(--text-soft);
}

.confirm-by-typing-input {
    max-width: 220px;
    font-family: monospace;
    font-size: 1.1em;
    font-weight: 700;
    letter-spacing: 3px;
    text-transform: uppercase;
    border: 2px solid var(--border);
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

    &::placeholder {
        color: var(--text-disabled);
        font-weight: 400;
        letter-spacing: 3px;
    }

    &:focus {
        border-color: var(--border-strong);
        box-shadow: none;
    }

    &.is-confirmed,
    &.is-confirmed:focus {
        border-color: $confirmed;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--success-fg) 15%, transparent);
    }
}

.confirm-by-typing-hint {
    margin-top: 10px;
    margin-bottom: 0;
    font-size: .9em;
    color: var(--text-muted);
}

.confirm-by-typing-footer {
    justify-content: space-between;
    border-top: 1px solid var(--border-subtle);
}
</style>
